import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { ParticipantLayout } from "../../components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import { 
  BookOpen, Play, FileText, CheckCircle, Clock, Award, Filter,
  GraduationCap, Calculator, Beaker, Briefcase, Wrench
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  "NTG": Beaker,
  "BWH": Briefcase,
  "ZIB": Calculator,
  "RBH": FileText,
  "MIKP": Wrench,
  "Prüfungsvorbereitung": GraduationCap,
  "Bootcamps": Award
};

const CATEGORY_COLORS = {
  "NTG": "bg-blue-500/10 text-blue-500 border-blue-500/30",
  "BWH": "bg-green-500/10 text-green-500 border-green-500/30",
  "ZIB": "bg-purple-500/10 text-purple-500 border-purple-500/30",
  "RBH": "bg-orange-500/10 text-orange-500 border-orange-500/30",
  "MIKP": "bg-red-500/10 text-red-500 border-red-500/30",
  "Prüfungsvorbereitung": "bg-yellow-500/10 text-yellow-500 border-yellow-500/30",
  "Bootcamps": "bg-pink-500/10 text-pink-500 border-pink-500/30"
};

const LernPlattform = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [coursesRes, categoriesRes, statsRes] = await Promise.all([
        axios.get(`${API}/courses`),
        axios.get(`${API}/courses/categories`),
        axios.get(`${API}/dashboard/participant-stats`)
      ]);
      setCourses(coursesRes.data);
      setCategories(categoriesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Kurse");
    } finally {
      setLoading(false);
    }
  };

  const filteredCourses = selectedCategory === "all" 
    ? courses 
    : courses.filter(c => c.category === selectedCategory);

  const CategoryIcon = ({ category }) => {
    const Icon = CATEGORY_ICONS[category] || BookOpen;
    return <Icon className="h-5 w-5" />;
  };

  return (
    <ParticipantLayout title="Lernplattform">
      <div className="space-y-6" data-testid="learning-platform">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <BookOpen className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_courses || 0}</p>
                  <p className="text-xs text-muted-foreground">Meine Kurse</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.courses_completed || 0}</p>
                  <p className="text-xs text-muted-foreground">Abgeschlossen</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <Play className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.courses_started || 0}</p>
                  <p className="text-xs text-muted-foreground">In Bearbeitung</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                  <Award className="h-5 w-5 text-yellow-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.average_progress?.toFixed(0) || 0}%</p>
                  <p className="text-xs text-muted-foreground">Fortschritt</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Category Filter */}
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Kategorien</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant={selectedCategory === "all" ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedCategory("all")}
              >
                Alle
              </Button>
              {categories.map(cat => (
                <Button
                  key={cat}
                  variant={selectedCategory === cat ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(cat)}
                  className="gap-1.5"
                >
                  <CategoryIcon category={cat} />
                  {cat}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Course Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1,2,3].map(i => (
              <Card key={i} className="bg-card border-border animate-pulse">
                <CardContent className="p-6 h-48"></CardContent>
              </Card>
            ))}
          </div>
        ) : filteredCourses.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="p-12 text-center">
              <BookOpen className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-medium mb-2">Keine Kurse verfügbar</h3>
              <p className="text-muted-foreground">
                Sie haben noch keinen Zugang zu Kursen. Kontaktieren Sie Ihren Administrator.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCourses.map(course => (
              <Card 
                key={course.id} 
                className="bg-card border-border hover:border-primary/50 transition-colors cursor-pointer group"
                onClick={() => navigate(`/lernplattform/${course.id}`)}
                data-testid={`course-card-${course.id}`}
              >
                {/* Course Thumbnail */}
                <div className="relative h-32 bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                  <CategoryIcon category={course.category} />
                  <div className="absolute top-2 right-2">
                    <Badge className={`${CATEGORY_COLORS[course.category] || "bg-muted"} border`}>
                      {course.category}
                    </Badge>
                  </div>
                  {course.progress_percent === 100 && (
                    <div className="absolute top-2 left-2">
                      <Badge className="bg-green-500 text-white">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Abgeschlossen
                      </Badge>
                    </div>
                  )}
                </div>
                
                <CardHeader className="pb-2">
                  <CardTitle className="text-base group-hover:text-primary transition-colors">
                    {course.title}
                  </CardTitle>
                  {course.description && (
                    <CardDescription className="line-clamp-2">
                      {course.description}
                    </CardDescription>
                  )}
                </CardHeader>
                
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Fortschritt</span>
                      <span className="font-medium">{course.progress_percent || 0}%</span>
                    </div>
                    <Progress value={course.progress_percent || 0} className="h-2" />
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{course.completed_items || 0} von {course.total_items || 0} Elementen</span>
                      <Button variant="ghost" size="sm" className="h-7 text-xs">
                        {course.progress_percent > 0 ? "Fortsetzen" : "Starten"}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </ParticipantLayout>
  );
};

export default LernPlattform;
